"use client";

import { Core } from "@faire/design-tokens";
import { Color } from "@faire/web--source/slate/Color";
import { Row, VerticalLine } from "@faire/web--source/slate/Layout";
import { TypographyStyles } from "@faire/web--source/slate/Typography";
import React from "react";
import styled, { css } from "styled-components";

import {
  highVisSlateComponent,
  HIGH_VIS_FILTER,
} from "../../utils/highVisSlateComponent";

export interface IProps {
  /** Text to be displayed in the badge. */
  content: string | number;
  /** Background color of the badge. */
  backgroundColor: Color;
  /** Border color of the badge. */
  borderColor?: Color;
  /** Applies the small Typography styles to the badge text content. */
  small?: boolean;
  /** Applies a larger border-radius to the corners of the badge. */
  rounded?: boolean;
  /** Text to be display before the content, separated by a divider. */
  prefix?: string;
  /** Sets the color of the divider between the prefix and the content. */
  dividerColor?: Color;
  /** Sets the color of the text. Will also set color of divider if not specified. */
  contentColor?: Color;
  /** Function to be triggered on badge hover */
  onMouseEnter?: () => void;
  className?: string;
  "data-test-id"?: string;
}

const BadgeBase: React.FC<IProps> = (props) => {
  const dividerColor =
    props.dividerColor ?? props.contentColor ?? Core.border.muted;

  return (
    <BadgeContainer
      $small={!!props.small}
      $rounded={!!props.rounded}
      $borderColor={props.borderColor}
      $backgroundColor={props.backgroundColor}
      $contentColor={props.contentColor}
      className={props.className}
      data-test-id={props["data-test-id"]}
      onMouseEnter={props.onMouseEnter}
    >
      {props.prefix ? (
        <>
          {props.prefix}
          <VerticalLine $color={dividerColor} $margin="0 6px" $height="12px" />
        </>
      ) : null}
      {props.content}
    </BadgeContainer>
  );
};

/**
 * @deprecated use Badge from slate/Badge
 * @deprecatedBy "@faire/web--source/slate/Badge/Badge#Badge"
 * @trackcomponent
 */
export const Badge = BadgeBase;

const BadgeContainer = styled(Row).attrs({
  align: "center",
  justify: "center",
})<{
  $small: boolean;
  $rounded: boolean;
  $borderColor?: Color;
  $backgroundColor: Color;
  $contentColor?: Color;
}>`
  ${(props) =>
    TypographyStyles({
      variant: props.$small ? "labelSansRegular" : "paragraphSansRegular",
      theme: props.theme,
    })};
  width: fit-content;
  min-width: 20px;
  min-height: 22px;
  padding: 0 4px;
  border: 1px solid ${(props) => props.$borderColor ?? props.$backgroundColor};
  background-color: ${(props) => props.$backgroundColor};
  color: ${(props) => props.$contentColor ?? Core.text.default};
  border-radius: ${(props) => (props.$rounded ? "999px" : "4px")};
  white-space: nowrap;

  ${(props) =>
    props.$small
      ? css`
          padding: 0 4px;
          ${VerticalLine} {
            height: 10px;
            margin: 0 4px;
          }
        `
      : ""}
  ${highVisSlateComponent(HIGH_VIS_FILTER.COMPONENT)}
`;
